import type { LobbyPlayer } from '@big2/shared';

/** Room-level facts that decide which numbers a lobby seat shows. */
export interface LobbyStatsContext {
  isTsaiRoom: boolean;
  minTsai: number;
  pointLimit?: number | null;
  isMuushig: boolean;
  isFinished: boolean;
  totals: Record<string, number>;
  lastTsaiTransfers?: Record<string, number> | null;
  penalties?: Record<string, number> | null;
  scoreDeltas?: Record<string, number> | null;
}

export type Tone = 'neutral' | 'good' | 'bad' | 'warning';

export interface LobbySeatStats {
  /** Tsai balance, when this is a Tsai room and the balance is known. */
  tsai: { balance: number; isLow: boolean } | null;
  /** Running points, when the room tracks them. */
  points: { total: number; limit: number | null; tone: Tone } | null;
  /** Last round's change: Tsai won/lost, Big Two penalty or Muushig score change. */
  delta: { value: number; text: string; tone: Tone } | null;
}

/** Muushig players start on this many points and count down. */
const MUUSHIG_STARTING_POINTS = 15;
/** Share of the point limit at which a total turns red, then amber. */
const POINT_DANGER_SHARE = 0.8;
const POINT_WARNING_SHARE = 0.6;

const signed = (n: number): string => (n > 0 ? `+${n}` : `${n}`);

function pointsTone(total: number, limit: number | null): Tone {
  if (!limit) return 'neutral';
  if (total >= limit * POINT_DANGER_SHARE) return 'bad';
  if (total >= limit * POINT_WARNING_SHARE) return 'warning';
  return 'neutral';
}

export function lobbySeatStats(player: LobbyPlayer, ctx: LobbyStatsContext): LobbySeatStats {
  const id = player.playerId;

  const tsai = ctx.isTsaiRoom && typeof player.tsaiBalance === 'number'
    ? { balance: player.tsaiBalance, isLow: !player.isBot && player.tsaiBalance < ctx.minTsai }
    : null;

  const total = ctx.totals[id] ?? (ctx.isMuushig ? MUUSHIG_STARTING_POINTS : 0);
  const limit = ctx.pointLimit || null;
  const points = ctx.isFinished || limit || ctx.isMuushig
    ? { total, limit, tone: pointsTone(total, limit) }
    : null;

  let delta: LobbySeatStats['delta'] = null;
  if (ctx.isFinished) {
    const tsaiDelta = ctx.lastTsaiTransfers?.[id];
    const penalty = ctx.penalties?.[id];
    const scoreDelta = ctx.scoreDeltas?.[id];
    if (ctx.isTsaiRoom && typeof tsaiDelta === 'number' && tsaiDelta !== 0) {
      delta = { value: tsaiDelta, text: signed(tsaiDelta), tone: tsaiDelta > 0 ? 'good' : 'bad' };
    } else if (ctx.isMuushig && typeof scoreDelta === 'number') {
      // Muushig counts down to zero, so gaining points is bad.
      delta = { value: scoreDelta, text: signed(scoreDelta), tone: scoreDelta > 0 ? 'bad' : 'good' };
    } else if (!ctx.isTsaiRoom && !ctx.isMuushig && typeof penalty === 'number') {
      delta = { value: penalty, text: `+${penalty}`, tone: 'bad' };
    }
  }

  return { tsai, points, delta };
}
